import { upgradeProperty } from '../../utils/upgradeProperty/upgradeProperty.js';
import { html } from '../../utils/lit/html.js';
import { http } from './services/http.js';
import { autoAffiliate } from '../../services/auto-affiliate/auto-affiliate.js';
import { getKebabCase } from '../../utils/getKebabCase/getKebabCase.js';
import gtmIdThemeMap from './services/gtmIdThemeMap.js';
import ContextService from '../../services/context/index.js';
import { migrateInitialData } from '../../utils/migrateInitialData/migrateInitialData.js';

const WIDGET_VERSION = '4.9.1'; // NOTE: This value should match the last version from the widget's CHANGELOG.md file.
export const WIDGET_NAME = 'oc-offers-widget';

const wEvent = (name, detail) =>
  new CustomEvent(`${WIDGET_NAME}:${name}`, {
    bubbles: true,
    composed: true,
    ...(detail ? { detail } : {})
  });

const isNonEmptyString = it => it && typeof it === 'string';

const eatIds = it =>
  it
    .split(',')
    .map(id => id.trim())
    .filter(Boolean)
    .join(',');

/**
 * Describes the variations that the widget supports.
 * Each variation represents a different way of rendering the widget. This includes both styles and functionality.
 *
 * Each variation is an object with the following properties:
 * - name: The name of the variation component.
 * - importScript: The function that will import the variation's script. This is used to lazy load the variation's script.
 * - cssParts: The list of CSS parts that the variation uses. This is necessary to expose the CSS parts to the variation's host.
 *
 * NOTE: The 'default' and 'grouped-offers' variations use the new standardized widgets.
 * The other variations still use legacy variation components in the `variations/` folder pending migration.
 */
const variations = {
  default: {
    name: 'oc-offers-carousel-widget',
    importScript: () => import('../oc-offers-carousel-widget/index.js')
  },
  'grouped-offers': {
    name: 'oc-offers-grouped-widget',
    importScript: () => import('../oc-offers-grouped-widget/index.js')
  },
  'horizontal-slim': {
    name: 'oc-offers-slim-widget',
    importScript: () => import('../oc-offers-slim-widget/index.js')
  }
};

const template = document.createElement('template');

template.innerHTML = html`
  <style>
    :host {
      box-sizing: border-box;
      display: block;
      width: 100%;
    }
  </style>

  <slot name="loading" hidden></slot>
  <slot name="error" hidden></slot>
`;

class OCOffersWidget extends HTMLElement {
  constructor() {
    super();

    if (!this.shadowRoot) {
      this.attachShadow({ mode: 'open' });
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }

    this._version = WIDGET_VERSION;
  }

  get vcId() {
    return this.getAttribute('vc-id');
  }

  set vcId(value) {
    this.setAttribute('vc-id', value);
  }

  get offerIds() {
    return this.getAttribute('offer-ids');
  }

  set offerIds(value) {
    this.setAttribute('offer-ids', value);
  }

  get zIndexPatch() {
    return this.getAttribute('z-index-patch');
  }

  set zIndexPatch(value) {
    this.setAttribute('z-index-patch', value);
  }

  get slotId() {
    return this.getAttribute('slot-id');
  }

  set slotId(value) {
    this.setAttribute('slot-id', value);
  }

  get overrideUrl() {
    return this.getAttribute('override-url');
  }

  set overrideUrl(value) {
    this.setAttribute('override-url', value);
  }

  get theme() {
    return this.getAttribute('theme');
  }

  set theme(value) {
    this.setAttribute('theme', value);
  }

  get disableAnalytics() {
    return this.hasAttribute('disable-analytics');
  }

  set disableAnalytics(value) {
    if (value) {
      this.setAttribute('disable-analytics', '');
    } else {
      this.removeAttribute('disable-analytics');
    }
  }

  get geotargeting() {
    return this.hasAttribute('geotargeting');
  }

  set geotargeting(value) {
    if (value) {
      this.setAttribute('geotargeting', '');
    } else {
      this.removeAttribute('geotargeting');
    }
  }

  get alwaysShowTCs() {
    return this.hasAttribute('alwaysShowTCs');
  }

  set alwaysShowTCs(value) {
    if (value) {
      this.setAttribute('alwaysShowTCs', '');
    } else {
      this.removeAttribute('alwaysShowTCs');
    }
  }

  get placeholders() {
    return this.getAttribute('placeholders');
  }

  set placeholders(value) {
    this.setAttribute('placeholders', value);
  }

  get variation() {
    return this.getAttribute('variation');
  }

  set variation(value) {
    this.setAttribute('variation', value);
  }

  get visibleOffersCount() {
    return this.getAttribute('visible-offers-count');
  }

  set visibleOffersCount(value) {
    this.setAttribute('visible-offers-count', value);
  }

  get sortBy() {
    return this.getAttribute('sort-by');
  }

  set sortBy(value) {
    this.setAttribute('sort-by', value);
  }

  get offerLimit() {
    return this.getAttribute('offer-limit');
  }

  set offerLimit(value) {
    this.setAttribute('offer-limit', value);
  }

  static get observedAttributes() {
    return ['theme', 'placeholders', 'disable-analytics', 'style'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    requestAnimationFrame(() =>
      this.#updateVariationAttribute(name, oldValue, newValue)
    );
  }

  #updateVariationAttribute(name, oldValue, newValue) {
    const variationComponent =
      this.shadowRoot.querySelector('[data-variation]');

    if (!variationComponent) {
      return;
    }

    const booleanAttrs = ['disable-analytics'];

    for (const attr of OCOffersWidget.observedAttributes) {
      if (name === attr && oldValue !== newValue) {
        if (booleanAttrs.includes(attr)) {
          if (newValue || newValue === '') {
            variationComponent.setAttribute(attr, '');
          } else {
            variationComponent.removeAttribute(attr);
          }
        } else {
          if (newValue) {
            variationComponent.setAttribute(attr, newValue);
          } else {
            variationComponent.removeAttribute(attr);
          }
        }
      }
    }
  }

  #getErrorSlot() {
    return this.shadowRoot.querySelector('slot[name="error"]');
  }

  async connectedCallback() {
    // Migrate any existing _initialData attribute and define property descriptor
    migrateInitialData(this);

    upgradeProperty(this, 'vcId');
    upgradeProperty(this, 'offerIds');
    upgradeProperty(this, 'slotId');
    upgradeProperty(this, 'overrideUrl');
    upgradeProperty(this, 'theme');
    upgradeProperty(this, 'disableAnalytics');
    upgradeProperty(this, 'geotargeting');
    upgradeProperty(this, 'placeholders');
    upgradeProperty(this, 'variation');
    upgradeProperty(this, 'visibleOffersCount');
    upgradeProperty(this, 'alwaysShowTCs');
    upgradeProperty(this, 'sortBy');
    upgradeProperty(this, 'offerLimit');
    upgradeProperty(this, 'zIndexPatch');
    upgradeProperty(this, '_initialData');

    const data = await this.#fetchData();

    ContextService({
      ...data,
      slotId: this.slotId,
      overrideUrl: this.overrideUrl
    });

    if (!data || !Array.isArray(data.offers) || !data.offers.length) {
      await new Promise(resolve => requestAnimationFrame(resolve));
      const errorSlot = this.#getErrorSlot();
      if (errorSlot.hidden) {
        this.hidden = true;
      }

      return;
    }

    if (this.slotId) {
      data.variantType = 'slot';
      data.idForCxCache = this.slotId;
    }

    if (this.vcId) {
      data.variantType = 'vcId';
      data.idForCxCache = this.vcId;
    }

    if (this.offerIds) {
      data.variantType = 'offerIds';
      data.idForCxCache = this.offerIds;
    }

    if (!data.gtmId) {
      data.gtmId = gtmIdThemeMap[this.theme];
    }

    this.#loadVariationComponent(data);

    autoAffiliate(
      this.theme,
      this.geotargeting,
      data.geolocation,
      data.bookmakersFromOffers?.map(b => b.code)
    );
  }

  setAttributesForSlotId(data, variationComponent) {
    variationComponent.setAttribute('variation', data.variation);
    variationComponent.setAttribute(
      'visible-offers-count',
      data.visibleOffersCount
    );

    if (data.theme !== '') {
      variationComponent.setAttribute('theme', data.theme);
    }

    const { disableAnalytics, autohideTerms } = data;

    Object.entries({
      disableAnalytics,
      autohideTerms
    }).forEach(([key, value]) => {
      if (value) {
        variationComponent.setAttribute(getKebabCase(key), '');
      }
    });

    const stringifiedPlaceholders = Object.entries(data.placeholders || {})
      .map(([key, value]) => {
        return `${key}=${value}`;
      })
      .join('|');
    variationComponent.setAttribute('placeholders', stringifiedPlaceholders);
  }

  async #loadVariationComponent(data) {
    try {
      // Set variation from this.variation or if it's using Slot ID (and not set) then take it from data.
      let variation = this.variation || data?.variation || 'default';

      const theVariation = variations[variation];

      if (!theVariation) {
        variation = 'default';
      }

      await theVariation.importScript();

      const variationComponent = document.createElement(theVariation.name);

      variationComponent.part = `variation-${variation}`;
      variationComponent.setAttribute('data-variation', variation);
      theVariation.cssParts &&
        variationComponent.setAttribute(
          'exportparts',
          theVariation.cssParts.join(',')
        );
      variationComponent._initialData = data;

      // Add attrs from data if it's using Slot ID
      if (this.attributes['slot-id']) {
        this.setAttributesForSlotId(data, variationComponent);
      }

      // Override attr instead, if present
      for (const attr of this.attributes) {
        variationComponent.setAttribute(attr.name, attr.value);
      }

      ['load', 'load-error', 'load-success'].forEach(status => {
        variationComponent.addEventListener(
          `${theVariation.name}:${status}`,
          data => this.dispatchEvent(wEvent(status, data))
        );
      });

      this.shadowRoot.appendChild(variationComponent);
    } catch (error) {
      console.error(error);
    }
  }

  async #fetchData() {
    const initialData = this._initialData;

    const loadingSlot = this.shadowRoot.querySelector('slot[name="loading"]');
    const errorSlot = this.shadowRoot.querySelector('slot[name="error"]');

    loadingSlot.hidden = false;

    try {
      let data = null;

      if (isNonEmptyString(this.slotId)) {
        data =
          initialData ??
          (await http.fetchWidget(this.slotId, this.overrideUrl));
      } else {
        const requestParams = {
          geotargeting: this.geotargeting
        };

        if (this.sortBy) {
          requestParams.sortBy = this.sortBy;
        }

        if (this.offerLimit) {
          requestParams.limit = this.offerLimit;
        }

        if (isNonEmptyString(this.vcId)) {
          requestParams.vcId = this.vcId;
          data = initialData ?? (await http.fetchOffers(requestParams));
        } else if (isNonEmptyString(this.offerIds)) {
          requestParams.offerIds = eatIds(this.offerIds);
          data = initialData ?? (await http.fetchOffers(requestParams));
        }
      }

      this.dispatchEvent(wEvent('load-success', { data }));
      return data;
    } catch (error) {
      errorSlot.hidden = false;
      this.dispatchEvent(wEvent('load-error', { error }));
    } finally {
      loadingSlot.hidden = true;
      this.dispatchEvent(wEvent('load'));
    }
  }
}

if (window.customElements && !window.customElements.get(WIDGET_NAME)) {
  window.customElements.define(WIDGET_NAME, OCOffersWidget);
}
